(function () {
  'use strict';

  const STORAGE_KEY = 'seo_url_params_v1';

  // Parameters that definitely don't affect content
  const KNOWN_TRACKING_PARAMS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'fbclid',
    'gclid',
    'msclkid',
    '_ga',
    'mc_cid',
    'mc_eid',
    'ref',
    'source',
    'affiliate',
    'tracking',
  ];

  // Parameters that might affect content
  const KNOWN_CONTENT_PARAMS = [
    'variant',
    'color',
    'size',
    'sort',
    'filter',
    'page',
    'view',
    'display',
    'layout',
  ];

  function getData() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored
        ? JSON.parse(stored)
        : {
            trackingParams: [...KNOWN_TRACKING_PARAMS],
            contentParams: [...KNOWN_CONTENT_PARAMS],
            learned: {},
          };
    } catch (_e) {
      return {
        trackingParams: [...KNOWN_TRACKING_PARAMS],
        contentParams: [...KNOWN_CONTENT_PARAMS],
        learned: {},
      };
    }
  }

  function saveData(data) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (_e) {
      console.warn('[URL Params] Storage failed');
    }
  }

  // Learn if a parameter affects content
  function learnParameter(param, affectsContent) {
    const data = getData();

    if (!data.learned[param]) {
      data.learned[param] = { samples: 0, affectsContent: 0 };
    }

    data.learned[param].samples++;
    if (affectsContent) {
      data.learned[param].affectsContent++;
    }

    // After 10 samples, classify parameter
    if (data.learned[param].samples >= 10) {
      const ratio = data.learned[param].affectsContent / data.learned[param].samples;

      if (ratio > 0.7) {
        // Affects content
        if (!data.contentParams.includes(param)) {
          data.contentParams.push(param);
        }
      } else if (ratio < 0.3) {
        // Tracking only
        if (!data.trackingParams.includes(param)) {
          data.trackingParams.push(param);
        }
      }
    }

    saveData(data);
  }

  // Get canonical URL by removing tracking parameters
  function getCanonicalURL() {
    const url = new URL(window.location.href);
    const data = getData();
    const paramsToRemove = [];

    // Remove known tracking parameters
    url.searchParams.forEach((value, key) => {
      if (data.trackingParams.includes(key.toLowerCase())) {
        paramsToRemove.push(key);
      }
    });

    paramsToRemove.forEach((param) => url.searchParams.delete(param));

    // Sort remaining parameters for consistency
    const sortedParams = new URLSearchParams([...url.searchParams.entries()].sort());

    return (
      url.origin +
      url.pathname +
      (sortedParams.toString() ? '?' + sortedParams.toString() : '') +
      url.hash
    );
  }

  // Update canonical tag
  function updateCanonicalTag() {
    const canonicalURL = getCanonicalURL();

    // Find existing canonical tag
    let canonical = document.querySelector('link[rel="canonical"]');

    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }

    // Only update if different from current
    if (canonical.href !== canonicalURL) {
      canonical.href = canonicalURL;
    }
  }

  // Monitor for DOM changes that indicate parameter affected content
  function monitorContentChanges() {
    const url = new URL(window.location.href);
    const params = [...url.searchParams.keys()];

    if (params.length === 0) return;

    // Store initial content hash
    const initialHash = hashContent();

    // Check if content changed after load (indicates dynamic content)
    setTimeout(() => {
      const finalHash = hashContent();
      const contentChanged = initialHash !== finalHash;

      // Learn from each parameter
      params.forEach((param) => {
        const data = getData();
        if (
          !data.trackingParams.includes(param.toLowerCase()) &&
          !data.contentParams.includes(param.toLowerCase())
        ) {
          learnParameter(param.toLowerCase(), contentChanged);
        }
      });
    }, 2000);
  }

  // Simple content hash for change detection
  function hashContent() {
    const mainContent = document.querySelector('main, #MainContent, .main-content');
    if (!mainContent) return '';

    const text = mainContent.textContent.substring(0, 1000);
    let hash = 0;
    for (let i = 0; i < text.length; i++) {
      const char = text.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    return hash.toString();
  }

  // Add structured data about parameter handling
  function addParameterStructuredData() {
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      url: getCanonicalURL(),
      significantLinks: [],
      isPartOf: {
        '@type': 'WebSite',
        url: window.location.origin,
      },
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);
  }

  // Clean URL in browser (optional, cosmetic only). Deliberately kept disabled: opt-in toggle
  // invoked by uncommenting the call in init() below; not dead code to delete.
  // eslint-disable-next-line no-unused-vars -- see comment above
  function cleanURLDisplay() {
    const canonicalURL = getCanonicalURL();

    if (window.location.href !== canonicalURL && 'replaceState' in window.history) {
      try {
        window.history.replaceState({}, '', canonicalURL);
      } catch (_e) {
        // Some browsers restrict history manipulation
      }
    }
  }

  // Initialize
  function init() {
    updateCanonicalTag();
    monitorContentChanges();
    addParameterStructuredData();

    // Optionally clean URL display (can be disabled if causes issues)
    // cleanURLDisplay();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Update on navigation (for SPAs)
  window.addEventListener('popstate', init);
})();
